/* eslint-disable no-console */
/* eslint-disable max-len */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { Skeleton, ConfigProvider } from 'antd';
import * as Sentry from '@sentry/react';
import { FallbackRender } from '@sentry/react';
import { locales } from '@config/locales';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { identifyHotjarUser, useAppSelector } from '@lib/index';
import { useRefreshPermissionsOnLoad } from '@hooks/index';
import { ENV, loadEnvConfiguration } from '@config/envConfig';
import ComponentRoutes from '../_routes';
import { persister } from './store';
import ErrorFallback from './errorFallback';
import AuthZero from './authZero';
import AuthAzure from './authAzure';
import HotjarRouteTracker from './HotjarRouteTracker';

type Language = 'en' | 'es';

declare global {
  interface Window {
    Intercom?: (...args: unknown[]) => void;
    intercomSettings?: Record<string, unknown>;
  }
}

const INTERCOM_SCRIPT_ID = 'intercom-widget-script';

const loadIntercomScript = (appId: string): Promise<void> => new Promise((resolve, reject) => {
  if (!appId) {
    resolve();
    return;
  }

  const existingScript = document.getElementById(INTERCOM_SCRIPT_ID) as HTMLScriptElement | null;

  if (existingScript) {
    if (existingScript.dataset.loaded === 'true') {
      resolve();
      return;
    }

    existingScript.addEventListener('load', () => resolve(), { once: true });
    existingScript.addEventListener('error', () => reject(new Error('Failed to load Intercom script')), { once: true });
    return;
  }

  const script = document.createElement('script');
  script.id = INTERCOM_SCRIPT_ID;
  script.src = `https://widget.intercom.io/widget/${appId}`;
  script.async = true;

  script.addEventListener('load', () => {
    script.dataset.loaded = 'true';
    resolve();
  }, { once: true });

  script.addEventListener('error', () => {
    reject(new Error('Failed to load Intercom script'));
  }, { once: true });

  document.body.appendChild(script);
});

const App = () => {
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState<Error | null>(null);
  const [isIntercomReady, setIsIntercomReady] = useState(false);

  const previousIdentityRef = useRef<string | null>(null);

  const {
    i18n: { language },
  } = useTranslation();

  const { currentUser } = useAppSelector((state) => state.account);

  useRefreshPermissionsOnLoad();

  useEffect(() => {
    let mounted = true;

    loadEnvConfiguration()
      .then(() => {
        if (mounted) {
          setIsConfigLoaded(true);
        }
      })
      .catch((error) => {
        if (mounted) {
          setConfigError(error as Error);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const hotJarId = ENV.VITE_HOTJAR_ID || import.meta.env.VITE_HOTJAR_ID;
  const hotJarEnabled = String(ENV.VITE_HOTJAR_ENABLED ?? import.meta.env.VITE_HOTJAR_ENABLED ?? 'true') === 'true';
  const googleTagManagerId = ENV.VITE_GOOGLE_TAG_MANAGER_ID || import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID;
  const intercomAppId = ENV.VITE_INTERCOM_APP_ID || import.meta.env.VITE_INTERCOM_APP_ID;
  const baseUrl = ENV.VITE_BASE_URL || import.meta.env.VITE_BASE_URL;

  const email = currentUser?.email;
  const firstName = currentUser?.firstName;
  const lastName = currentUser?.lastName;
  const roleLabel = currentUser?.roleLabel;
  const userId = currentUser?.userId ?? null;

  const platformHost = useMemo(() => window.location.hostname, []);

  const clientName = useMemo(() => {
    try {
      const url = new URL(baseUrl);
      return url.hostname.split('.')[0] || '';
    } catch {
      return '';
    }
  }, [baseUrl]);

  const intercomSettings = useMemo(() => {
    const apiHost = (() => {
      try {
        return new URL(baseUrl).hostname;
      } catch {
        return '';
      }
    })();

    const customAttributes: Record<string, unknown> = {
      api_host: apiHost,
      client_name: clientName,
      platform_host: platformHost,
    };

    if (typeof roleLabel === 'string' && roleLabel.trim()) {
      customAttributes.site_name = roleLabel.trim();
    }

    const settings: Record<string, unknown> = {
      api_base: 'https://api-iam.intercom.io',
      app_id: intercomAppId,
      custom_attributes: customAttributes,
    };

    const companyId = platformHost || clientName;
    if (companyId) {
      settings.company = {
        id: companyId,
        name: clientName || platformHost,
      };
    }

    const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim();
    if (fullName) {
      settings.name = fullName;
    }

    if (typeof email === 'string' && email.trim()) {
      settings.email = email.trim();
    }

    if (userId !== null && userId !== undefined && String(userId).trim()) {
      settings.user_id = `${platformHost || clientName}:${String(userId).trim()}`;
    }

    return settings;
  }, [baseUrl, clientName, email, firstName, intercomAppId, lastName, platformHost, roleLabel, userId]);

  const identityKey = useMemo(() => {
    const normalizedEmail = typeof email === 'string' ? email.trim() : '';
    const normalizedUserId = userId !== null && userId !== undefined ? String(userId).trim() : '';

    if (normalizedEmail || normalizedUserId) {
      return `identified:${platformHost || clientName}:${normalizedUserId}:${normalizedEmail}`;
    }

    return `visitor:${platformHost || clientName}`;
  }, [clientName, email, platformHost, userId]);

  useEffect(() => {
    let cancelled = false;

    if (!intercomAppId || !isConfigLoaded) {
      return undefined;
    }

    loadIntercomScript(intercomAppId)
      .then(() => {
        if (!cancelled) {
          setIsIntercomReady(true);
        }
      })
      .catch((error) => {
        console.error('Intercom script failed to load', error);
      });

    return () => {
      cancelled = true;
    };
  }, [intercomAppId, isConfigLoaded]);

  useEffect(() => {
    if (!isConfigLoaded || !intercomAppId || !isIntercomReady || typeof window.Intercom !== 'function') {
      return;
    }

    const identityChanged = previousIdentityRef.current !== identityKey;

    if (identityChanged) {
      window.Intercom('shutdown');
    }

    window.intercomSettings = intercomSettings;
    window.Intercom('boot', intercomSettings);

    previousIdentityRef.current = identityKey;
  }, [identityKey, intercomAppId, intercomSettings, isConfigLoaded, isIntercomReady]);

  useEffect(() => () => {
    if (typeof window.Intercom === 'function') {
      window.Intercom('shutdown');
    }
  }, []);

  useEffect(() => {
    if (!isConfigLoaded || !hotJarEnabled || !userId) return;

    identifyHotjarUser(`${platformHost || clientName}:${String(userId).trim()}`, {
      client_name: clientName,
      platform_host: platformHost,
      role: currentUser?.role,
      role_label: currentUser?.roleLabel,
      site_id: currentUser?.siteId,
      contractor_id: currentUser?.contractorId,
      section_id: currentUser?.sectionId,
      parent_site_id: currentUser?.parentSiteId,
      site_country: currentUser?.siteCountry,
    });
  }, [clientName, currentUser, hotJarEnabled, isConfigLoaded, platformHost, userId]);

  if (configError) {
    throw configError;
  }

  if (!isConfigLoaded) {
    return <Skeleton />;
  }

  return (
    <ConfigProvider locale={locales[language as Language]}>
      <PersistGate loading={<Skeleton />} persistor={persister}>
        <Sentry.ErrorBoundary fallback={ErrorFallback as FallbackRender}>
          <Helmet>
            {hotJarEnabled && hotJarId && (
              <script>
                {`
                  (function(h,o,t,j,a,r){
                    h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                    h._hjSettings={hjid:${hotJarId},hjsv:6};
                    a=o.getElementsByTagName('head')[0];
                    r=o.createElement('script');r.async=1;
                    r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                    a.appendChild(r);
                  })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
                `}
              </script>
            )}

            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${googleTagManagerId}`}
            />
            <script>
              {`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${googleTagManagerId}');
              `}
            </script>
          </Helmet>

          <AuthZero>
            <AuthAzure>
              <BrowserRouter>
                <HotjarRouteTracker />
                <Routes>
                  <Route path="/*" element={<ComponentRoutes />} />
                </Routes>
              </BrowserRouter>
            </AuthAzure>
          </AuthZero>
        </Sentry.ErrorBoundary>
      </PersistGate>
    </ConfigProvider>
  );
};

export default App;
